import React, { useState, useEffect } from 'react';
import { FiDownload, FiCalendar, FiCreditCard, FiCheckCircle, FiClock } from 'react-icons/fi';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import html2pdf from 'html2pdf.js';
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";


const SubscriptionHistory = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    fetchSubscriptionHistory();
  }, []);

  const fetchSubscriptionHistory = async () => {
    try {
      const response = await api.get('/vendor/bills');
      if (response.data.success) {
        setSubscriptions(response.data.data);
      }
    } catch (error) {
      toast.error('Failed to fetch subscription history');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const downloadBill = async (billId) => {
    setDownloadingId(billId);

    try {
      const response = await api.get(`/vendor/bills/${billId}/download`);
      if (!response.data.success) return;

      const { bill, vendor, plan, generator } = response.data.data;

      const doc = new jsPDF({
        unit: "mm",
        format: [80, 200],
      });

      // 🔥 Mono font for perfect alignment
      doc.setFont("courier", "normal");

      const formatAmount = (amt) =>
        `Rs. ${Number(amt || 0).toFixed(2)}`;

      let y = 6;
      const centerX = 40;

      const line = () => {
        doc.setLineWidth(0.2);
        doc.line(4, y, 76, y);
        y += 3;
      };

      // ================= HEADER =================
      doc.setFontSize(12);
      doc.setFont("courier", "bold");
      doc.text(
        vendor?.restaurantDetails?.restaurantName || "RESTAURANT",
        centerX,
        y,
        { align: "center" }
      );

      y += 5;

      doc.setFontSize(9);
      doc.setFont("courier", "normal");
      doc.text("SUBSCRIPTION BILL", centerX, y, { align: "center" });

      y += 4;

      doc.setFontSize(8);
      doc.text(vendor?.restaurantDetails?.address || "-", centerX, y, {
        align: "center",
        maxWidth: 70,
      });

      y += 4;

      doc.text(
        `GST: ${(vendor?.restaurantDetails?.gstNumber || "N/A").toUpperCase()}`,
        centerX,
        y,
        { align: "center" }
      );

      y += 3;
      line();

      // ================= FROM =================
      doc.setFontSize(8);
      doc.setFont("courier", "bold");
      doc.text("From", 5, y);
      y += 4;

      const labelX = 5;
      const rightX = 75; // 🔥 right edge

      // Name
      doc.setFont("courier", "normal");
      doc.text("Name:", labelX, y);
      doc.setFont("courier", "normal");
      doc.text(
        generator?.name || "-",
        rightX,
        y,
        { align: "right" }
      );
      y += 3;

      // GST
      doc.setFont("courier", "normal");
      doc.text("GST:", labelX, y);
      doc.setFont("courier", "normal");
      doc.text(
        (generator?.gstNumber || "N/A").toUpperCase(),
        rightX,
        y,
        { align: "right" }
      );
      y += 3;

      // Mobile
      doc.setFont("courier", "normal");
      doc.text("Mobile:", labelX, y);
      doc.setFont("courier", "normal");
      doc.text(
        generator?.contactNumber || "N/A",
        rightX,
        y,
        { align: "right" }
      );
      y += 3;

      line();

      // ================= TO =================
      doc.setFontSize(8);
      doc.setFont("courier", "bold");
      doc.text("To", 5, y);
      y += 4;



      // Name
      doc.setFont("courier", "normal");
      doc.text("Name:", labelX, y);
      doc.setFont("courier", "normal");
      doc.text(
        vendor?.restaurantDetails?.restaurantName || "-",
        rightX,
        y,
        { align: "right" }
      );
      y += 3;

      // GST
      doc.setFont("courier", "normal");
      doc.text("GST:", labelX, y);
      doc.setFont("courier", "normal");
      doc.text(
        (vendor?.restaurantDetails?.gstNumber || "N/A").toUpperCase(),
        rightX,
        y,
        { align: "right" }
      );
      y += 3;

      // Mobile
      doc.setFont("courier", "normal");
      doc.text("Mobile:", labelX, y);
      doc.setFont("courier", "normal");
      doc.text(
        vendor?.personalDetails?.contactNumber || "N/A",
        rightX,
        y,
        { align: "right" }
      );
      y += 3;

      line();

      // ================= BILL DETAILS =================
      doc.setFontSize(8);
      doc.setFont("courier", "bold");
      doc.text("Bill Details", 5, y);
      y += 4;


      // Bill #
      doc.setFont("courier", "normal");
      doc.text("Bill #:", labelX, y);
      doc.setFont("courier", "normal");
      doc.text(bill?.billNumber || "-", rightX, y, { align: "right" });
      y += 3;

      // Date
      doc.setFont("courier", "normal");
      doc.text("Date:", labelX, y);
      doc.setFont("courier", "normal");
      doc.text(
        new Date(bill?.createdAt).toLocaleDateString("en-IN") +
        " " +
        new Date(bill?.createdAt).toLocaleTimeString("en-IN", {
          hour: "2-digit",
          minute: "2-digit",
        }),
        rightX,
        y,
        { align: "right" }
      );
      y += 3;

      // Due
      doc.setFont("courier", "normal");
      doc.text("Valid Till:", labelX, y);
      doc.setFont("courier", "normal");
      doc.text(
        new Date(bill?.dueDate).toLocaleDateString("en-IN"),
        rightX,
        y,
        { align: "right" }
      );
      y += 3;

      line();

      // ================= ITEMS =================
      doc.setFontSize(8);
      doc.setFont("courier", "bold");
      doc.text("Plan Name", 5, y);
      y += 4;

      const itemName = `${plan?.name || "Subscription"} Plan`;
      const price = formatAmount(bill?.pricing?.basePrice);

      // 🔥 Left: Item | Right: Price
      doc.setFont("courier", "normal");

      // Item name (left)
      doc.text(itemName, 5, y);

      // Price (right aligned)
      doc.text(price, 75, y, { align: "right" });

      y += 3;

      line();
      // ================= TOTAL =================
      doc.setFont("courier", "normal");

      doc.text("Subtotal", 5, y);
      doc.text(formatAmount(bill?.pricing?.basePrice), 75, y, { align: "right" });
      y += 4;

      if (bill?.pricing?.gstAmount) {
        doc.text("CGST (9%)", 5, y);
        doc.text(formatAmount(bill.pricing.gstAmount / 2), 75, y, { align: "right" });
        y += 4;

        doc.text("SGST (9%)", 5, y);
        doc.text(formatAmount(bill.pricing.gstAmount / 2), 75, y, { align: "right" });
        y += 3;
      }

      line();
      y += 3;

      // 🔥 TOTAL BOLD
      doc.setFont("courier", "bold");
      doc.text("TOTAL", 5, y);
      doc.text(formatAmount(bill?.pricing?.totalAmount), 75, y, { align: "right" });

      y += 3;
      line();
      y += 3;

      // ================= STATUS =================
      doc.setFont("courier", "bold");
      doc.setFontSize(10);
      doc.text(
        `STATUS: ${(bill?.status || "UNKNOWN").toUpperCase()}`,
        centerX,
        y,
        { align: "center" }
      );

      y += 5;

      // ================= FOOTER =================
      doc.setFont("courier", "normal");
      doc.setFontSize(8);
      doc.text("Thank you for your business!", centerX, y, {
        align: "center",
      });

      y += 3;

      doc.text(
        `Generated: ${new Date().toLocaleString("en-IN")}`,
        centerX,
        y,
        { align: "center" }
      );

      // ================= SAVE =================
      doc.save(
        `bill-${bill?.billNumber || bill?._id?.slice(-6)}.pdf`
      );

      toast.success("Bill downloaded successfully!");
    } catch (error) {
      toast.error("Failed to download bill");
      console.error(error);
    } finally {
      setDownloadingId(null);
    }
  };
  const getStatusBadge = (bill) => {
    const now = new Date();
    const dueDate = new Date(bill.dueDate);
    const isActive = bill.status === 'paid' || (bill.status === 'pending' && now <= dueDate);

    if (bill.status === 'paid') {
      return {
        text: 'Paid',
        color: '#4caf50',
        bgColor: '#e8f5e9',
        icon: <FiCheckCircle />
      };
    } else if (bill.status === 'pending' && now <= dueDate) {
      return {
        text: 'Pending',
        color: '#ff9800',
        bgColor: '#fff3e0',
        icon: <FiClock />
      };
    } else {
      return {
        text: 'Overdue',
        color: '#f44336',
        bgColor: '#ffebee',
        icon: <FiClock />
      };
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const calculateDuration = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50 p-4 md:p-6">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl shadow-lg p-8 mb-8 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16"></div>
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-white opacity-10 rounded-full -ml-12 -mb-12"></div>

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <FiCreditCard className="text-3xl" />
            <h1 className="text-3xl md:text-4xl font-bold">Subscription History</h1>
          </div>
          <p className="text-lg opacity-90">
            View all your past and current subscriptions with downloadable receipts
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center text-blue-500">
              <FiCreditCard size={20} />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {subscriptions.length}
              </div>
              <div className="text-sm text-gray-600">Total Subscriptions</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center text-green-500">
              <FiCheckCircle size={20} />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {subscriptions.filter(sub => new Date(sub.endDate) > new Date()).length}
              </div>
              <div className="text-sm text-gray-600">Active Subscriptions</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center text-orange-500">
              <FiClock size={20} />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {subscriptions.filter(sub => new Date(sub.endDate) <= new Date()).length}
              </div>
              <div className="text-sm text-gray-600">Expired Subscriptions</div>
            </div>
          </div>
        </div>
      </div>

      {/* Subscriptions List */}
      {subscriptions.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-16 text-center">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <FiCreditCard className="text-gray-400 text-3xl" />
          </div>
          <h3 className="text-xl font-semibold text-gray-700 mb-3">
            No subscriptions found
          </h3>
          <p className="text-gray-500 text-sm">
            You haven't subscribed to any plan yet
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-5 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Plan Name</th>
                  <th className="px-5 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Bill Date</th>
                  <th className="px-5 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Due Date</th>
                  <th className="px-5 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Duration</th>
                  <th className="px-5 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Amount</th>
                  <th className="px-5 py-4 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">Status</th>
                  <th className="px-5 py-4 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">Bill</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {subscriptions.map((bill) => {
                  const status = getStatusBadge(bill);
                  return (
                    <tr
                      key={bill._id}
                      className="hover:bg-gray-50 transition-colors duration-150"
                    >
                      <td className="px-5 py-5">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center text-blue-500">
                            <FiCreditCard size={18} />
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900 text-sm">
                              {bill.planDetails?.name || 'N/A'}
                            </div>
                            <div className="text-xs text-gray-500">
                              Bill ID: {bill._id.slice(-8)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-5">
                        <div className="flex items-center gap-2 text-gray-600 text-sm">
                          <FiCalendar size={14} />
                          <span>{formatDate(bill.billDate || bill.createdAt)}</span>
                        </div>
                      </td>
                      <td className="px-5 py-5">
                        <div className="flex items-center gap-2 text-gray-600 text-sm">
                          <FiCalendar size={14} />
                          <span>{formatDate(bill.dueDate)}</span>
                        </div>
                      </td>
                      <td className="px-5 py-5">
                        <span className="px-3 py-1 bg-gray-100 rounded-md text-xs font-semibold text-gray-600">
                          {bill.planDetails?.duration || 'N/A'} days
                        </span>
                      </td>
                      <td className="px-5 py-5">
                        <div className="font-bold text-blue-600 text-base">
                          ₹{bill.pricing?.totalAmount || 0}
                        </div>
                      </td>
                      <td className="px-5 py-5 text-center">
                        <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${status.text === 'Paid' ? 'bg-green-100 text-green-800' :
                          status.text === 'Pending' ? 'bg-amber-100 text-amber-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                          {status.icon}
                          {status.text}
                        </span>
                      </td>
                      <td className="px-5 py-5 text-center">
                        <button
                          onClick={() => downloadBill(bill._id, bill.planDetails?.name)}
                          disabled={downloadingId === bill._id}
                          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all duration-200 ${downloadingId === bill._id
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700 hover:shadow-lg transform hover:-translate-y-0.5'
                            }`}
                        >
                          {downloadingId === bill._id ? (
                            <>
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                              Downloading...
                            </>
                          ) : (
                            <>
                              <FiDownload size={14} />
                              Download Bill
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionHistory;
